import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import * as NativeBiometric from '@capgo/capacitor-native-biometric';


const BIOMETRIC_SERVER = 'cogitationes.5searches.com';

export interface BiometricAvailability {
  available: boolean;
  label: string; // "Fingerprint" | "Face ID" | "Biometric" etc.
}

export interface StoredCredentials {
  username: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class BiometricService {
  private availability: BiometricAvailability | null = null;
  private plugin: typeof NativeBiometric | null = null;

  private async ensurePlugin(): Promise<typeof NativeBiometric | null> {
    if (this.plugin) return this.plugin;
    if (!Capacitor.isNativePlatform()) return null;
    try {
      this.plugin = NativeBiometric;
      return this.plugin;
    } catch {
      return null;
    }
  }

  private biometryTypeToLabel(biometryType: string | number | undefined): string {
    const t = String(biometryType ?? '').toLowerCase();
    if (t === 'faceid' || t === 'face') return 'Face ID';
    if (t === 'fingerprint' || t === 'finger') return 'Fingerprint';
    if (t === 'iris') return 'Iris';
    return 'Biometric';
  }

  /** Call once on app init or before showing biometric login. */
  async checkAvailability(): Promise<BiometricAvailability> {
    if (this.availability !== null) return this.availability;
    if (!Capacitor.isNativePlatform()) {
      this.availability = { available: false, label: '' };
      return this.availability;
    }
    try {
      const mod = await this.ensurePlugin();
      if (!mod) return { available: false, label: '' };
      const result = await mod.NativeBiometric.isAvailable({ useFallback: false });
      const label = result.isAvailable ? this.biometryTypeToLabel(result.biometryType) : '';
      this.availability = { available: result.isAvailable === true, label };
      return this.availability;
    } catch {
      return { available: false, label: '' };
    }
  }

  /** Whether we have credentials stored for biometric login. */
  async hasStoredCredentials(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const mod = await this.ensurePlugin();
      if (!mod) return false;
      const creds = await mod.NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
      return !!(creds?.username && creds?.password);
    } catch {
      return false;
    }
  }

  /** Get stored credentials after biometric verification (shows biometric prompt). */
  async getCredentials(): Promise<StoredCredentials | null> {
    const mod = await this.ensurePlugin();
    if (!mod) return null;
    try {
      await mod.NativeBiometric.verifyIdentity({
        reason: 'Sign in to Cogitationes',
        title: 'Biometric sign in',
      });
      const creds = await mod.NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
      if (creds?.username && creds?.password) {
        return { username: creds.username, password: creds.password };
      }
      return null;
    } catch {
      return null;
    }
  }

  /** Store credentials for biometric login (call after successful password login). */
  async setCredentials(username: string, password: string): Promise<boolean> {
    const mod = await this.ensurePlugin();
    if (!mod) return false;
    try {
      const { isAvailable } = await mod.NativeBiometric.isAvailable({ useFallback: false });
      if (!isAvailable) return false;
      await mod.NativeBiometric.setCredentials({
        username,
        password,
        server: BIOMETRIC_SERVER,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Verify identity with biometric prompt, then store credentials.
   * Use on first-time login so the user sees the biometric prompt and credentials are registered.
   * @param label e.g. "Fingerprint" or "Face ID" from checkAvailability() for the prompt title.
   */
  async verifyThenSetCredentials(username: string, password: string, label: string): Promise<boolean> {
    const mod = await this.ensurePlugin();
    if (!mod) return false;
    try {
      const { isAvailable } = await mod.NativeBiometric.isAvailable({ useFallback: false });
      if (!isAvailable) return false;
      await mod.NativeBiometric.verifyIdentity({
        reason: 'Save your login for quick sign-in next time',
        title: label ? `Save with ${label}` : 'Save with Biometric',
      });
      await mod.NativeBiometric.setCredentials({
        username,
        password,
        server: BIOMETRIC_SERVER,
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Remove stored biometric credentials (e.g. on logout). */
  async clearCredentials(): Promise<void> {
    const mod = await this.ensurePlugin();
    if (!mod) return;
    try {
      await mod.NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch {
      // ignore
    }
  }
}
